import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Pencil, Trash2 } from 'lucide-react';
import type { Budget } from '@/lib/types';
import { useCurrency } from '@/hooks/useCurrency';

interface BudgetCardProps {
  budget: Budget;
  onEdit: (budget: Budget) => void;
  onDelete: (id: string) => void;
}

function getProgressColor(percentage: number): string {
  if (percentage >= 85) return 'text-red-600';
  if (percentage >= 60) return 'text-yellow-600';
  return 'text-green-600';
}

function getProgressBarClass(percentage: number): string {
  if (percentage >= 85) return '[&>div]:bg-red-500';
  if (percentage >= 60) return '[&>div]:bg-yellow-500';
  return '[&>div]:bg-green-500';
}

export default function BudgetCard({ budget, onEdit, onDelete }: BudgetCardProps) {
  const { fmt } = useCurrency();
  // Calculate total budgeted and spent from categories
  const totalBudgeted = budget.categories.reduce((sum, c) => sum + c.budgeted, 0);
  const totalSpent = budget.categories.reduce((sum, c) => sum + c.spent, 0);
  const remaining = totalBudgeted - totalSpent;
  const percentage = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0;
  const clampedPercentage = Math.min(percentage, 100);

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardContent className="p-0">
        {/* Color accent bar */}
        <div
          className="h-1.5 w-full rounded-t-lg"
          style={{ backgroundColor: budget.color || '#3b82f6' }}
        />
        <div className="p-4 pb-2">
      <div className="flex items-start justify-between space-y-0">
        <div className="flex items-center gap-3">
          <span
            className="h-10 w-10 rounded-lg border flex items-center justify-center text-lg flex-shrink-0"
            style={{ backgroundColor: budget.color ? `${budget.color}15` : '#eff6ff' }}
          >
            {budget.emoji || budget.name.charAt(0).toUpperCase()}
          </span>
          <div>
            <CardTitle className="text-base font-semibold">{budget.name}</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {budget.month}/{budget.year} &middot; {budget.budgetMethod}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8 text-muted-foreground hover:text-primary" onClick={() => onEdit(budget)} aria-label="Edit budget">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(budget.id)} aria-label="Delete budget">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
        </div>
      <CardContent className="space-y-3 pt-0">
        {/* Overall progress */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">
              {fmt(totalSpent)} /{' '}
              {fmt(totalBudgeted)}
            </span>
            <span className={`text-sm font-semibold ${getProgressColor(percentage)}`}>
              {percentage}%
            </span>
          </div>
          <Progress value={clampedPercentage} className={`h-2 ${getProgressBarClass(percentage)}`} />
          <p className="text-xs text-muted-foreground mt-1">
            {remaining >= 0
              ? `${fmt(remaining)} remaining`
              : `${fmt(Math.abs(remaining))} over budget`}
          </p>
        </div>

        {/* Category breakdown */}
        {budget.categories.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            {budget.categories.slice(0, 4).map((cat, i) => {
              const catPct = cat.budgeted > 0 ? Math.round((cat.spent / cat.budgeted) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{cat.categoryName}</span>
                    <span className={getProgressColor(catPct)}>
                      {fmt(cat.spent)} / {fmt(cat.budgeted)}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(catPct, 100)}
                    className={`h-1.5 mt-0.5 ${getProgressBarClass(catPct)}`}
                  />
                </div>
              );
            })}
            {budget.categories.length > 4 && (
              <p className="text-xs text-muted-foreground">
                +{budget.categories.length - 4} more categories
              </p>
            )}
          </div>
        )}
      </CardContent>
      </CardContent>
    </Card>
  );
}
